import React, { useState, useEffect } from 'react';

const DaycareTransferPackageModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  warning,              // For age-based transfers (from warnings)
  enrollment,           // For general transfers (from enrollment table)
  isGeneralTransfer = false,  // Flag to indicate general transfer vs age transfer
  apiCall,
  showError,
  showSuccess 
}) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [keepDiscount, setKeepDiscount] = useState(true);
  const [manualDiscountAmount, setManualDiscountAmount] = useState(0);
  const [manualDiscountReason, setManualDiscountReason] = useState('');
  const [transferReason, setTransferReason] = useState('');

  // Determine data source (warning or enrollment)
  const data = warning || enrollment;

  useEffect(() => {
    if (isOpen && data) {
      loadSuitablePackages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  const loadSuitablePackages = async () => {
    setLoading(true);
    try {
      if (isGeneralTransfer) {
        // General transfer: Load ALL active packages (not just age-appropriate)
        const allPackages = await apiCall(`/daycare/packages?academicYear=${data.AcademicYear || '25-26'}`);
        setPackages(allPackages || []);
      } else {
        // Age transfer: Load only age-appropriate packages
        // ✅ FIXED: Use correct URL with /for-child/ 
        const agePackages = await apiCall(`/daycare/packages/for-child/${data.ChildID}?academicYear=${data.AcademicYear || '25-26'}`);
        setPackages(agePackages || []);
      }
    } catch (error) {
      showError('Error loading packages: ' + error.message);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedPackage) {
      showError('Please select a package');
      return;
    }

    if (isGeneralTransfer && !transferReason.trim()) {
      showError('Please provide a reason for transfer');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Cancel current enrollment using the correct /cancel endpoint
      // This endpoint handles both canceling the enrollment AND canceling future charges
      await apiCall(`/daycare/enrollments/${data.EnrollmentID}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({
          cancelReason: isGeneralTransfer 
            ? `Transfer to ${selectedPackage.PackageName} - ${transferReason}` 
            : `Transfer to ${selectedPackage.PackageName} - Age adjustment`,
          endDate: new Date().toISOString().split('T')[0],
          cancelFutureCharges: true,  // This cancels all unpaid monthly charges
          forgiveBalance: false
        })
      });

      // Step 2: Create new enrollment
      const discountAmount = keepDiscount ? (data.DiscountAmount || 0) : manualDiscountAmount;
      const discountReason = keepDiscount 
        ? (data.DiscountReason || 'Transferred from previous package')
        : manualDiscountReason;

      await apiCall('/daycare/enrollments', {
        method: 'POST',
        body: JSON.stringify({
          childId: data.ChildID,
          familyId: data.FamilyID,
          packageId: selectedPackage.PackageID,
          academicYear: data.AcademicYear || '25-26',
          startDate: startDate,
          manualDiscountAmount: discountAmount,
          manualDiscountReason: discountReason
        })
      });

      // Step 3: Resolve warning (only if age-based transfer)
      if (!isGeneralTransfer && warning) {
        await apiCall(`/daycare/warnings/${warning.WarningID}/resolve`, {
          method: 'PUT',
          body: JSON.stringify({
            notes: `Transferred to ${selectedPackage.PackageName}${isGeneralTransfer ? ` - Reason: ${transferReason}` : ''}`
          })
        });
      }

      const childName = `${data.FirstName || data.ChildName} ${data.LastName || ''}`.trim();
      showSuccess(`${childName} transferred to ${selectedPackage.PackageName} successfully!`);
      onSuccess();
      handleClose();
    } catch (error) {
      showError('Error transferring package: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPackage(null);
    setStartDate(new Date().toISOString().split('T')[0]);
    setKeepDiscount(true);
    setManualDiscountAmount(0);
    setManualDiscountReason('');
    setTransferReason('');
    onClose();
  };

  if (!isOpen || !data) return null;

  const childName = data.FirstName || data.ChildName || 'Child';
  const currentPackage = data.PackageName;
  const currentAge = data.CurrentAge;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">
              {isGeneralTransfer ? 'Transfer Package' : 'Transfer Package (Age Issue)'}
            </h2>
            <p className="text-orange-100 text-sm mt-1">
              {isGeneralTransfer 
                ? `Transfer ${childName} to a different package`
                : `Transfer ${childName} to a suitable package`
              }
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Current Info */}
        <div className="px-6 py-4 bg-orange-50 border-b border-orange-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Child:</span>
              <span className="ml-2 font-semibold text-gray-900">{childName} {data.LastName || ''}</span>
            </div>
            {currentAge && (
              <div>
                <span className="text-gray-600">Current Age:</span>
                <span className="ml-2 font-semibold text-gray-900">{currentAge} months</span>
              </div>
            )}
            <div>
              <span className="text-gray-600">Current Package:</span>
              <span className="ml-2 font-semibold text-gray-900">{currentPackage}</span>
            </div>
            {!isGeneralTransfer && data.PackageMinAge && data.PackageMaxAge && (
              <div>
                <span className="text-gray-600">Age Range:</span>
                <span className="ml-2 font-semibold text-orange-600">
                  {data.PackageMinAge}-{data.PackageMaxAge} months
                </span>
              </div>
            )}
          </div>
          {!isGeneralTransfer && data.WarningMessage && (
            <div className="mt-3 p-2 bg-orange-100 rounded border border-orange-300">
              <p className="text-sm text-orange-800">
                ⚠️ {data.WarningMessage}
              </p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Available Packages */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {isGeneralTransfer ? 'Available Packages' : 'Suitable Packages for Age'}
                </h3>
                
                {packages.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-600 mb-2">
                      {isGeneralTransfer 
                        ? 'No packages available'
                        : `No suitable packages found`
                      }
                    </p>
                    <p className="text-sm text-gray-500">
                      {isGeneralTransfer 
                        ? 'Please check package availability'
                        : `There are no packages available for ${childName}'s current age`
                      }
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {packages.map((pkg) => (
                      <button
                        key={pkg.PackageID}
                        onClick={() => setSelectedPackage(pkg)}
                        disabled={pkg.HasAvailability === 0}
                        className={`
                          text-left p-4 rounded-lg border-2 transition-all
                          ${selectedPackage?.PackageID === pkg.PackageID
                            ? 'border-orange-500 bg-orange-50 shadow-md'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                          }
                          ${pkg.HasAvailability === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{pkg.PackageName}</h4>
                          {selectedPackage?.PackageID === pkg.PackageID && (
                            <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-600">
                            <span className="font-medium">Age:</span> {pkg.MinAgeMonths}-{pkg.MaxAgeMonths} months
                          </p>
                          <p className="text-gray-600">
                            <span className="font-medium">Time:</span> {pkg.StartTime} - {pkg.EndTime}
                          </p>
                          <p className="text-gray-600">
                            <span className="font-medium">Capacity:</span> {pkg.AvailableSpots}/{pkg.MaxCapacity} spots
                          </p>
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-2xl font-bold text-orange-600">
                            ${pkg.MonthlyRate?.toFixed(2) || '0.00'}
                            <span className="text-sm font-normal text-gray-500">/month</span>
                          </p>
                        </div>

                        {pkg.HasAvailability === 0 && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                              Full - No spots available
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Transfer Details */}
              {selectedPackage && (
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800">Transfer Details</h3>
                  
                  {/* Transfer Reason (General transfers only) */}
                  {isGeneralTransfer && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Transfer <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={transferReason}
                        onChange={(e) => setTransferReason(e.target.value)}
                        placeholder="e.g., Parent wants different time slot, needs more hours..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        required
                      />
                    </div>
                  )}

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transfer Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>

                  {/* Discount Options */}
                  <div className="border-t border-gray-300 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Options
                    </label>
                    
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={keepDiscount}
                          onChange={() => setKeepDiscount(true)}
                          className="mr-2"
                        />
                        <span className="text-sm">
                          Keep current discount 
                          {(data.DiscountAmount || 0) > 0 && (
                            <span className="font-semibold text-green-600 ml-1">
                              (${(data.DiscountAmount || 0).toFixed(2)})
                            </span>
                          )}
                        </span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!keepDiscount}
                          onChange={() => setKeepDiscount(false)}
                          className="mr-2"
                        />
                        <span className="text-sm">Set new discount</span>
                      </label>
                    </div>

                    {!keepDiscount && (
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Amount ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={selectedPackage.MonthlyRate}
                            value={manualDiscountAmount}
                            onChange={(e) => setManualDiscountAmount(parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Reason</label>
                          <input
                            type="text"
                            value={manualDiscountReason}
                            onChange={(e) => setManualDiscountReason(e.target.value)}
                            placeholder="e.g., Transfer discount"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cost Summary */}
                  <div className="border-t border-gray-300 pt-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">New Monthly Rate:</span>
                        <span className="font-medium">${selectedPackage.MonthlyRate?.toFixed(2) || '0.00'}</span>
                      </div>
                      {(keepDiscount ? (data.DiscountAmount || 0) : manualDiscountAmount) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Discount:</span>
                          <span className="text-red-600 font-medium">
                            -${((keepDiscount ? (data.DiscountAmount || 0) : manualDiscountAmount)).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                        <span>New Monthly Cost:</span>
                        <span className="text-orange-600">
                          ${((selectedPackage.MonthlyRate || 0) - (keepDiscount ? (data.DiscountAmount || 0) : manualDiscountAmount)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleTransfer}
            disabled={!selectedPackage || loading}
            className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Transferring...' : 'Transfer Package'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DaycareTransferPackageModal;